import React, { useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { update as updateCandidate } from "../../models/CandidatesModel";
import { apiError } from "../../helpers/ErrorHelper";
import { checkPermission } from "../../helpers/GeneralHelper";
import { buildOnboardPrefillFromCandidate } from "../../helpers/CandidateNextActionHelper";
import { buildCandidateKanbanUpdatePayload } from "../../helpers/RequirementKanbanHelper";

export default function StatusSuggestionModal({
  show,
  onHide,
  suggestion,
  candidate,
  userToken,
  onApplied,
}) {
  const [saving, setSaving] = useState(false);

  if (!suggestion || !candidate) return null;

  const canEdit = checkPermission("candidates", "edit");
  const canAddOnboard = checkPermission("onboards", "add");

  const onboardCreateLink = {
    pathname: "/onboards/create",
    state: {
      module: {
        title: "Onboards",
        singularTitle: "Onboard",
        key: "onboards",
        route: "onboards",
        primaryCol: "_onboard_id",
      },
      prefill: buildOnboardPrefillFromCandidate(candidate),
    },
  };

  const applyStatus = async () => {
    if (!canEdit || !userToken || !suggestion.suggestedStatus?.value) {
      onHide?.();
      return;
    }

    const payload = buildCandidateKanbanUpdatePayload(
      candidate,
      suggestion.suggestedStatus.value
    );
    if (!payload) return;

    setSaving(true);
    try {
      const response = await updateCandidate(
        userToken,
        candidate._candidate_id,
        payload
      );
      if (response?.status) {
        toast.success(`Candidate status updated to ${suggestion.suggestedStatus.label}.`);
        onApplied?.(suggestion.suggestedStatus);
        onHide?.();
      } else {
        toast.error(response?.message || "Could not update candidate status.");
      }
    } catch (error) {
      apiError(error);
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{suggestion.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">{suggestion.message}</p>
        <div className="status-suggestion-compare">
          <div>
            <span className="text-muted small d-block">Current</span>
            <span className="badge bg-light text-dark border">
              {suggestion.currentStatusName}
            </span>
          </div>
          <i className="ri-arrow-right-line text-muted mx-2" />
          <div>
            <span className="text-muted small d-block">Suggested</span>
            <span className="badge bg-primary-transparent text-primary">
              {suggestion.suggestedStatus.label}
            </span>
          </div>
        </div>
        <p className="text-muted small mb-0 mt-3">
          This is a suggestion only — nothing changes unless you confirm.
        </p>
      </Modal.Body>
      <Modal.Footer className="flex-wrap gap-2">
        <button type="button" className="btn btn-light" onClick={onHide} disabled={saving}>
          Not now
        </button>
        {suggestion.showOnboardAction && canAddOnboard && (
          <Link
            to={onboardCreateLink}
            className="btn btn-outline-primary"
            onClick={onHide}
          >
            <i className="ri-user-add-line me-1" />
            Start onboard
          </Link>
        )}
        {canEdit ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={applyStatus}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm align-middle me-1" />
                Updating…
              </>
            ) : (
              <>
                <i className="ri-check-line me-1" />
                Update status
              </>
            )}
          </button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
